import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { format } from "date-fns";
import { CheckCircle2, MailWarning, Send, Trash2, Users } from "lucide-react";

import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  broadcastStatusUpdate,
  listStatusSubscribers,
  removeStatusSubscriber,
  resendStatusConfirmation,
  SEVERITY_VALUES,
} from "@/lib/status-subscribers.functions";

export const Route = createFileRoute(
  "/_authenticated/app/settings/status-subscribers",
)({
  head: () => ({
    meta: [
      { title: "Status subscribers — CairnOS" },
      {
        name: "description",
        content:
          "Manage email subscribers for the public status page and broadcast incident updates.",
      },
    ],
  }),
  component: StatusSubscribersPage,
});

type Subscriber = Awaited<ReturnType<typeof listStatusSubscribers>>[number];

function StatusSubscribersPage() {
  const { tenantId } = useScope();
  const qc = useQueryClient();

  const listFn = useServerFn(listStatusSubscribers);
  const removeFn = useServerFn(removeStatusSubscriber);
  const resendFn = useServerFn(resendStatusConfirmation);
  const broadcastFn = useServerFn(broadcastStatusUpdate);

  const { data: subs = [], isLoading } = useQuery({
    queryKey: ["status-subs", tenantId],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId! } }),
  });

  const stats = useMemo(() => {
    const total = subs.length;
    const confirmed = subs.filter(
      (s) => s.confirmed_at && !s.unsubscribed_at,
    ).length;
    const pending = subs.filter(
      (s) => !s.confirmed_at && !s.unsubscribed_at,
    ).length;
    const unsubscribed = subs.filter((s) => !!s.unsubscribed_at).length;
    return { total, confirmed, pending, unsubscribed };
  }, [subs]);

  const remove = useMutation({
    mutationFn: async (id: string) => {
      if (!tenantId) throw new Error("No tenant");
      return removeFn({ data: { tenantId, id } });
    },
    onSuccess: () => {
      toast.success("Subscriber removed");
      qc.invalidateQueries({ queryKey: ["status-subs", tenantId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const resend = useMutation({
    mutationFn: async (id: string) => {
      if (!tenantId) throw new Error("No tenant");
      return resendFn({ data: { tenantId, id } });
    },
    onSuccess: (r) =>
      toast.success(`Confirmation link ready for ${r.email}`, {
        description: `Token: ${r.confirmationToken.slice(0, 12)}…`,
      }),
    onError: (e: Error) => toast.error(e.message),
  });

  // Broadcast dialog state
  const [open, setOpen] = useState(false);
  const [severity, setSeverity] = useState<(typeof SEVERITY_VALUES)[number]>(
    "major",
  );
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const broadcast = useMutation({
    mutationFn: async () => {
      if (!tenantId) throw new Error("No tenant");
      return broadcastFn({
        data: {
          tenantId,
          severity,
          subject,
          message,
          affectedComponents: [],
        },
      });
    },
    onSuccess: (r) => {
      toast.success(
        `Queued ${r.sent} notification${r.sent === 1 ? "" : "s"} (${r.skipped} skipped)`,
      );
      setOpen(false);
      setSubject("");
      setMessage("");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="container mx-auto p-6 max-w-6xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Users className="h-6 w-6" /> Status page subscribers
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Customers who opted in for incident notifications from your public
            status page.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} disabled={!stats.confirmed}>
          <Send className="h-4 w-4 mr-2" /> Broadcast update
        </Button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={<Users className="h-4 w-4" />} label="Total" value={stats.total} />
        <StatCard
          icon={<CheckCircle2 className="h-4 w-4 text-success" />}
          label="Confirmed"
          value={stats.confirmed}
        />
        <StatCard
          icon={<MailWarning className="h-4 w-4 text-warning" />}
          label="Pending"
          value={stats.pending}
        />
        <StatCard
          icon={<Trash2 className="h-4 w-4 text-muted-foreground" />}
          label="Unsubscribed"
          value={stats.unsubscribed}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Subscribers</CardTitle>
          <CardDescription>
            Public visitors subscribe via the form at <code>/status?tenant=…</code>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : subs.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No subscribers yet. Share your status page link to start
              collecting them.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Severities</TableHead>
                  <TableHead>Subscribed</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subs.map((s: Subscriber) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">{s.email}</TableCell>
                    <TableCell>{stateBadge(s)}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(s.notify_severities ?? []).map((sev) => (
                          <Badge
                            key={sev}
                            variant="outline"
                            className="text-[10px] uppercase"
                          >
                            {sev}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {format(new Date(s.created_at), "PP")}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      {!s.confirmed_at && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => resend.mutate(s.id)}
                          disabled={resend.isPending}
                        >
                          Resend
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Remove ${s.email}`}
                        onClick={() => remove.mutate(s.id)}
                        disabled={remove.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Broadcast status update</DialogTitle>
            <DialogDescription>
              Queue a notification to every confirmed subscriber that opted into
              this severity.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Severity</Label>
              <Select value={severity} onValueChange={(v) => setSeverity(v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITY_VALUES.map((sev) => (
                    <SelectItem key={sev} value={sev} className="capitalize">
                      {sev}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Subject</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Partial outage — order ingest delayed"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Message</Label>
              <Textarea
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="We are investigating an issue affecting…"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => broadcast.mutate()}
              disabled={broadcast.isPending || !subject || !message}
            >
              {broadcast.isPending ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <Card>
      <CardContent className="py-4 flex items-center gap-3">
        <div className="rounded-md border p-2 bg-muted/40">{icon}</div>
        <div>
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="text-2xl font-semibold tabular-nums">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function stateBadge(s: Subscriber) {
  if (s.unsubscribed_at)
    return <Badge variant="outline" className="text-[10px]">Unsubscribed</Badge>;
  if (!s.confirmed_at)
    return (
      <Badge className="bg-warning/15 text-warning text-[10px]">Pending</Badge>
    );
  return (
    <Badge className="bg-success/15 text-success text-[10px]">Confirmed</Badge>
  );
}
